import React, { useState, useEffect, useCallback, useMemo } from "react";
import { FiAlertCircle, FiInfo, FiRefreshCw } from "react-icons/fi";
import { Bar } from "react-chartjs-2";
import { fetchEventPointsSummary } from "../../../utils/eventApi";
import { compact, shortDay, niceCeiling, RankedBars } from "../../components/DashboardCharts";

/**
 * The whole event's points on one screen: how many in total, which source
 * they came from, and which day.
 *
 * `participants` answers this one user at a time; this answers it for the
 * event — "was yesterday bigger than today, and was that games or mining?".
 *
 * ── The six groups ─────────────────────────────────────────────────────────
 *
 * The API returns eight buckets. Streak bonus is part of the mining rate, and
 * Creator Hub and admin corrections are both rare, so they are folded into
 * Mining and "Creator Hub & other" — eight hues stacked in one bar cannot be
 * told apart, six can. The card under each group still shows the split.
 *
 * Colours are categorical slots 1–6 in fixed order, assigned by group and
 * never by rank, validated for colour-vision deficiency (worst adjacent ΔE 9.1,
 * target ≥ 8). Aqua, yellow and magenta sit below 3:1 on white, so the chart
 * always has a legend and the day table carries every value as text.
 */
export const GROUPS = [
  {
    key: "mining",
    label: "Mining",
    color: "#2a78d6",
    value: (s) => (s.daily_mining || 0) + (s.streak_bonus || 0),
  },
  { key: "referral", label: "Referral", color: "#eb6834", value: (s) => s.referral || 0 },
  { key: "games", label: "Games", color: "#1baf7a", value: (s) => s.mini_game || 0 },
  { key: "tasks", label: "Tasks", color: "#eda100", value: (s) => s.task || 0 },
  { key: "checkin", label: "Daily check-in", color: "#e87ba4", value: (s) => s.daily_check_in || 0 },
  {
    key: "other",
    label: "Creator Hub & other",
    color: "#008300",
    value: (s) => (s.creator_hub || 0) + (s.other || 0),
  },
];

/** Points are decimal: mining converts PX at a rate, so 12.5 is a real value. */
const fmt = (v) => (Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });
const pct = (part, whole) => (whole > 0 ? `${((part / whole) * 100).toFixed(1)}%` : "0%");
const plural = (n, one, many = `${one}s`) => `${fmt(n)} ${Number(n) === 1 ? one : many}`;
const dayLabel = (d) => `Day ${d.day_number} · ${shortDay(d.day)}`;

/** The day with the most points for one group, or null if it earned nothing. */
export function bestDayOf(days, value) {
  let best = null;
  for (const d of days) {
    const v = value(d);
    if (v > 0 && (!best || v > best.value)) best = { day: d, value: v };
  }
  return best;
}

/** The line under each source card: what the group is made of. */
function detailFor(group, src, awards) {
  const a = awards || {};
  switch (group) {
    case "mining":
      return `Base ${fmt(src.daily_mining)} · Streak bonus ${fmt(src.streak_bonus)}`;
    case "referral":
      return a.referral_join?.count
        ? `${plural(a.referral_join.count, "join bonus", "join bonuses")} (${fmt(a.referral_join.points)}) · the rest is referral mining`
        : "Referral mining";
    case "games":
      return `${plural(a.game?.count || 0, "play")} rewarded`;
    case "tasks":
      return `${plural(a.task?.count || 0, "completion")}`;
    case "checkin":
      return `${plural(a.daily_check_in?.count || 0, "check-in")}`;
    case "other":
      return `Creator Hub ${fmt(src.creator_hub)} · Admin adjustments ${fmt(src.other)}`;
    default:
      return null;
  }
}

const StatTile = ({ label, value, foot }) => (
  <div className="p-4 rounded-xl border border-gray-100 bg-white">
    <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{label}</p>
    <p className="mt-1 text-xl font-bold text-gray-900">{value}</p>
    {foot && <p className="mt-1 text-[11px] text-gray-500">{foot}</p>}
  </div>
);

/** The stacked day chart. One bar per UTC day, one segment per group. */
const DaysChart = ({ days }) => {
  const data = useMemo(
    () => ({
      labels: days.map((d) => shortDay(d.day)),
      datasets: GROUPS.map((g) => ({
        label: g.label,
        data: days.map((d) => g.value(d.by_source)),
        backgroundColor: g.color,
        // The 2px surface gap between stacked segments.
        borderColor: "#ffffff",
        borderWidth: { top: 2, right: 0, bottom: 0, left: 0 },
        borderSkipped: false,
        maxBarThickness: 28,
        categoryPercentage: 0.72,
        barPercentage: 0.92,
      })),
    }),
    [days]
  );

  const peak = Math.max(
    0,
    ...days.map((d) => GROUPS.reduce((sum, g) => sum + Math.max(0, g.value(d.by_source)), 0))
  );
  const { max, ticks } = niceCeiling(peak);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    animation: { duration: 240 },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        mode: "index",
        intersect: false,
        backgroundColor: "rgba(11,11,11,0.94)",
        titleColor: "#ffffff",
        bodyColor: "#ffffff",
        footerColor: "#ffffff",
        padding: { top: 8, bottom: 8, left: 10, right: 12 },
        cornerRadius: 6,
        titleFont: { size: 11, weight: "600" },
        bodyFont: { size: 11 },
        footerFont: { size: 11, weight: "600" },
        usePointStyle: true,
        boxWidth: 8,
        boxHeight: 8,
        boxPadding: 4,
        // Biggest source first, and quiet sources left out, so the reader sees
        // what drove the day rather than six rows of mostly zeros.
        itemSort: (a, b) => b.parsed.y - a.parsed.y,
        filter: (item) => item.parsed.y !== 0,
        callbacks: {
          title: (items) => (items[0] ? dayLabel(days[items[0].dataIndex]) : ""),
          label: (ctx) => `${ctx.dataset.label}: ${fmt(ctx.parsed.y)}`,
          footer: (items) =>
            items[0] ? `Total: ${fmt(days[items[0].dataIndex].total)} pts` : "",
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        border: { color: "#e1e0d9" },
        ticks: { color: "#898781", font: { size: 10 }, maxRotation: 0, autoSkip: true, autoSkipPadding: 16 },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        max,
        grid: { color: "#e1e0d9", drawTicks: false },
        border: { display: false },
        ticks: {
          color: "#898781",
          font: { size: 10 },
          padding: 6,
          stepSize: max / ticks,
          callback: (value) => compact(value),
        },
      },
    },
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mb-3 text-[11px] text-gray-500">
        {GROUPS.map((g) => (
          <span key={g.key} className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: g.color }} />
            {g.label}
          </span>
        ))}
      </div>
      <div className="h-56 sm:h-64">
        <Bar data={data} options={options} />
      </div>
    </div>
  );
};

const PointsSummaryTab = ({ eventId }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState("date");

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchEventPointsSummary(eventId);
      setSummary(res.data || null);
    } catch (err) {
      setError(err.message || "Could not load the points summary");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    load();
  }, [load]);

  const days = useMemo(() => summary?.days || [], [summary]);
  const sortedDays = useMemo(
    () => (sortBy === "points" ? [...days].sort((a, b) => b.total - a.total) : days),
    [days, sortBy]
  );

  if (error && !summary) {
    return (
      <div className="p-6">
        <div className="p-4 rounded-xl bg-red-50 border border-red-100 text-xs text-red-700 flex items-center gap-2">
          <FiAlertCircle /> {error}
        </div>
      </div>
    );
  }

  if (!summary) {
    return <div className="p-6 py-16 text-center text-xs text-gray-400">Loading points summary…</div>;
  }

  const { totals, by_source: src, awards, games } = summary;
  const earned = totals.earned_points;
  const bestDay = bestDayOf(days, (d) => d.total);
  // The biggest day per group, so the table can mark it.
  const groupBest = Object.fromEntries(
    GROUPS.map((g) => [g.key, bestDayOf(days, (d) => g.value(d.by_source))])
  );
  const rankedGroups = [...GROUPS].sort((a, b) => b.value(src) - a.value(src));

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-800">Points Summary</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            Every point earned in this event, by source and by day. Days are UTC.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          title="Reload"
          className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition disabled:opacity-40"
        >
          <FiRefreshCw size={15} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile
          label="Total points earned"
          value={fmt(earned)}
          foot={
            totals.exchanged_points > 0
              ? `${fmt(totals.exchanged_points)} exchanged · ${fmt(totals.current_points)} still held by users`
              : "Before any Event Exchange"
          }
        />
        <StatTile
          label="Participants"
          value={fmt(totals.participants)}
          foot={totals.blocked_participants > 0 ? `${fmt(totals.blocked_participants)} blocked` : "Users with any points"}
        />
        <StatTile label="Average per participant" value={fmt(totals.average_points)} foot="Total ÷ participants" />
        <StatTile
          label="Best day"
          value={bestDay ? fmt(bestDay.value) : "—"}
          foot={bestDay ? `${dayLabel(bestDay.day)} · ${plural(bestDay.day.active_users, "user")}` : "No points yet"}
        />
      </div>

      {totals.held_points > 0 && (
        <div className="p-3 rounded-xl bg-amber-50 border border-amber-100 text-xs text-amber-800 flex items-start gap-2">
          <FiInfo className="mt-0.5 shrink-0" />
          <span>
            {fmt(totals.held_points)} points earned by {plural(totals.held_users, "blocked user")} are held and not
            counted above. They are added or discarded when the user is unblocked.
          </span>
        </div>
      )}

      {/* By source */}
      <section>
        <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Where points came from</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {rankedGroups.map((g) => {
            const value = g.value(src);
            const best = groupBest[g.key];
            return (
              <div key={g.key} className="p-4 rounded-xl border border-gray-100 bg-white">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="flex items-center gap-2 text-xs font-medium text-gray-700">
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: g.color }} />
                    {g.label}
                  </span>
                  <span className="text-[11px] text-gray-500 tabular-nums">{pct(value, earned)}</span>
                </div>
                <p className="mt-1.5 text-lg font-bold text-gray-900 tabular-nums">{fmt(value)}</p>
                <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: earned > 0 ? `${Math.max(0, Math.min(100, (value / earned) * 100))}%` : "0%",
                      backgroundColor: g.color,
                    }}
                  />
                </div>
                <p className="mt-2 text-[11px] text-gray-500">{detailFor(g.key, src, awards)}</p>
                <p className="mt-0.5 text-[11px] text-gray-400">
                  {best ? `Best day: ${dayLabel(best.day)} (${fmt(best.value)})` : "No points yet"}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Games */}
      {games.length > 0 && (
        <section>
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Points by game</h4>
          <div className="p-4 rounded-xl border border-gray-100 bg-white">
            <RankedBars
              accent={GROUPS.find((g) => g.key === "games").color}
              rows={games.map((g) => ({
                key: g.game_key,
                label: g.name,
                value: Math.round(g.points * 100) / 100,
                foot: `${plural(g.plays, "play")} · ${pct(g.points, src.mini_game)} of game points`,
              }))}
            />
          </div>
        </section>
      )}

      {/* By day */}
      <section>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Points per day</h4>
          <div className="flex items-center gap-1 text-xs">
            <span className="text-gray-400 mr-1">Sort</span>
            {[
              ["date", "By date"],
              ["points", "Most points"],
            ].map(([key, label]) => (
              <button
                key={key}
                onClick={() => setSortBy(key)}
                className={`px-2.5 py-1 rounded-lg font-medium transition ${
                  sortBy === key ? "bg-indigo-50 text-indigo-700" : "text-gray-500 hover:bg-gray-100"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {days.length === 0 ? (
          <div className="p-8 rounded-xl border border-dashed border-gray-200 text-center text-xs text-gray-400">
            The event has not started yet, so there are no days to show.
          </div>
        ) : (
          <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
            <div className="p-4 border-b border-gray-100">
              <DaysChart days={days} />
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-100">
                    <th className="px-4 py-2.5 font-medium">Day</th>
                    <th className="px-4 py-2.5 font-medium text-right" title="Users who earned any points that day">
                      Users
                    </th>
                    {GROUPS.map((g) => (
                      <th key={g.key} className="px-4 py-2.5 font-medium text-right whitespace-nowrap">
                        {g.label}
                      </th>
                    ))}
                    <th className="px-4 py-2.5 font-medium text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedDays.map((d) => {
                    const isBest = bestDay && bestDay.day.day === d.day;
                    return (
                      <tr
                        key={d.day}
                        className={`border-b border-gray-50 ${isBest ? "bg-blue-50/50" : "hover:bg-gray-50/70"}`}
                      >
                        <td className="px-4 py-2.5 whitespace-nowrap">
                          <span className="font-medium text-gray-700">Day {d.day_number}</span>
                          <span className="text-gray-400"> · {shortDay(d.day)}</span>
                          {isBest && (
                            <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase bg-blue-100 text-blue-700">
                              Top day
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{fmt(d.active_users)}</td>
                        {GROUPS.map((g) => {
                          const v = g.value(d.by_source);
                          const top = groupBest[g.key] && groupBest[g.key].day.day === d.day;
                          return (
                            <td
                              key={g.key}
                              title={top ? `Best day for ${g.label}` : undefined}
                              className={`px-4 py-2.5 text-right tabular-nums ${
                                top ? "font-semibold text-gray-900 underline decoration-dotted underline-offset-2" : v ? "text-gray-600" : "text-gray-300"
                              }`}
                            >
                              {fmt(v)}
                            </td>
                          );
                        })}
                        <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-gray-900">
                          {fmt(d.total)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50/70 font-semibold text-gray-800">
                    <td className="px-4 py-2.5">All days</td>
                    <td className="px-4 py-2.5" />
                    {GROUPS.map((g) => (
                      <td key={g.key} className="px-4 py-2.5 text-right tabular-nums">
                        {fmt(g.value(src))}
                      </td>
                    ))}
                    <td className="px-4 py-2.5 text-right tabular-nums">{fmt(earned)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <p className="px-4 py-2.5 text-[10px] text-gray-400 border-t border-gray-100">
              Underlined: the best day for that source. Users = participants who earned any points that day.
            </p>
          </div>
        )}
      </section>
    </div>
  );
};

export default PointsSummaryTab;
